// Lead Detail Panel Component (Right Sidebar)

import { useState } from 'react';
import { useMapStore, useUserStore } from '../../stores';
import type { Business, WebsiteTemplate, Client } from '../../types';
import { 
  cn, 
  formatDate, 
  formatRelativeTime, 
  getLeadTypeLabel, 
  getLeadTypeColor,
  getWebsiteStatusLabel, 
  getWebsiteStatusColor,
  getCategoryIcon,
  getLeadScoreColor,
  getLeadScoreLabel,
  validateEmail,
  validatePhone
} from '../../lib/utils';
import { api } from '../../lib/api';
import { 
  X, 
  Globe, 
  Phone, 
  Mail, 
  MapPin, 
  Star, 
  MessageCircle, 
  AlertTriangle, 
  Target,
  Building2,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  ArrowUpRight,
  Edit,
  Plus,
  Check,
  Loader2,
  ExternalLink,
  Globe as GlobeIcon,
  Clock,
  ChevronDown,
  ChevronUp,
  Download,
  Copy,
  Share2,
  Heart,
  Flag,
  MoreVertical
} from 'lucide-react';

interface LeadDetailPanelProps {
  lead: Business | null;
  onClose: () => void;
  activeTab: 'details' | 'website' | 'contact' | 'analytics';
  onTabChange: (tab: 'details' | 'website' | 'contact' | 'analytics') => void;
}

export function LeadDetailPanel({ lead, onClose, activeTab, onTabChange }: LeadDetailPanelProps) {
  const { updateLead, mapState } = useMapStore();
  const { user, addNotification, createClient, addClient, websiteBuilds, addWebsiteBuild } = useUserStore();
  
  const [isEnriching, setIsEnriching] = useState(false);
  const [isBuildingWebsite, setIsBuildingWebsite] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WebsiteTemplate | null>(null);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [templates, setTemplates] = useState<WebsiteTemplate[]>([]);
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [clientForm, setClientForm] = useState({
    buildFee: 299,
    monthlyFee: 49,
    notes: '',
  });
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    contact: true,
    marketing: true,
    social: true,
    hours: false,
    analytics: false,
  });

  if (!lead) {
    return (
      <div className="flex flex-col h-full bg-white/95 backdrop-blur-xl">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No Lead Selected</h3>
            <p className="text-slate-500 mt-1">Click on a lead from the map or sidebar to view details</p>
          </div>
        </div>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleEnrichLead = async () => {
    setIsEnriching(true);
    try {
      const enriched = await api.enrichLead(lead);
      updateLead(lead.id, enriched);
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'success',
        title: 'Lead Enriched',
        message: `Additional data found for ${lead.name}`,
        read: false,
        createdAt: new Date().toISOString(),
      });
    } catch (error) {
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'error',
        title: 'Enrichment Failed',
        message: 'Could not enrich lead data',
        read: false,
        createdAt: new Date().toISOString(),
      });
    } finally {
      setIsEnriching(false);
    }
  };

  const handleBuildWebsite = async () => {
    if (!selectedTemplate) return;
    setIsBuildingWebsite(true);
    try {
      const build = await api.buildWebsite(lead.id, selectedTemplate.id, {});
      addWebsiteBuild(build);
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'success',
        title: 'Website Preview Ready',
        message: `Preview generated for ${lead.name}`,
        read: false,
        actionUrl: build.previewUrl,
        createdAt: new Date().toISOString(),
      });
      setShowTemplateSelector(false);
    } catch (error) {
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'error',
        title: 'Build Failed',
        message: 'Could not generate website preview',
        read: false,
        createdAt: new Date().toISOString(),
      });
    } finally {
      setIsBuildingWebsite(false);
    }
  };

  const handleCreateClient = async () => {
    try {
      const client = createClient({
        businessId: lead.id,
        business: lead,
        userId: user?.id || '',
        status: 'prospect',
        buildFee: clientForm.buildFee,
        monthlyFee: clientForm.monthlyFee,
        notes: clientForm.notes,
        lastContact: new Date().toISOString(),
      });
      updateLead(lead.id, { leadType: 'multiple' });
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'success',
        title: 'Client Added',
        message: `${lead.name} added to your pipeline`,
        read: false,
        createdAt: new Date().toISOString(),
      });
      setShowCreateClient(false);
    } catch (error) {
      addNotification({
        id: `notif-${Date.now()}`,
        userId: user?.id || '',
        type: 'error',
        title: 'Failed to Add Client',
        message: 'Could not create client record',
        read: false,
        createdAt: new Date().toISOString(),
      });
    }
  };

  const loadTemplates = async () => {
    const ts = await api.getTemplates();
    setTemplates(ts);
    setShowTemplateSelector(true);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    addNotification({
      id: `notif-${Date.now()}`,
      userId: user?.id || '',
      type: 'success',
      title: 'Copied',
      message: `${label} copied to clipboard`,
      read: false,
      createdAt: new Date().toISOString(),
    });
  };

  if (!lead) return null;

  return (
    <aside className="absolute inset-0 right-0 w-96 bg-white/95 backdrop-blur-xl border-l border-slate-200/50 shadow-xl z-40 flex flex-col animate-slide-in-right">
      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-slate-200/50 sticky top-0 bg-white/95 backdrop-blur-xl z-10">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
            <span className="text-2xl">{getCategoryIcon(lead.category)}</span>
          </div>
          <div className="min-w-0">
            <h2 className="font-semibold text-slate-900 truncate">{lead.name}</h2>
            <p className="text-sm text-slate-500 truncate">{lead.address}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => copyToClipboard(lead.name, 'Business name')}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Copy Name"
          >
            <Copy className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-slate-200/50 bg-white/50 sticky top-16 z-10">
        <nav className="flex p-1" role="tablist">
          {[
            { id: 'details', label: 'Details', icon: Building2, badge: undefined },
            { id: 'website', label: 'Website', icon: Globe, badge: lead.hasWebsite ? undefined : 'No Site' },
            { id: 'contact', label: 'Contact', icon: MessageCircle, badge: undefined },
            { id: 'analytics', label: 'Analytics', icon: TrendingUp, badge: undefined },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id as any)}
              className={cn(
                'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-sm'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              )}
              role="tab"
              aria-selected={activeTab === tab.id}
            >
              <tab.icon className="w-4 h-4" />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge && (
                <span className={cn(
                  'px-1.5 py-0.5 rounded-full text-xs',
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-200 text-slate-600'
                )}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Details Tab */}
        {activeTab === 'details' && (
          <div className="space-y-4 animate-fade-in">
            {/* Lead Score & Type Badges */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('badge px-3 py-1.5 text-sm', getLeadTypeColor(lead.leadType))}>
                {getLeadTypeLabel(lead.leadType)}
              </span>
              <span className={cn('badge px-3 py-1.5 text-sm', getWebsiteStatusColor(lead.websiteStatus))}>
                {getWebsiteStatusLabel(lead.websiteStatus)}
              </span>
              <div className={cn('ml-auto flex items-center gap-2 px-3 py-1.5 rounded-full', getLeadScoreColor(lead.leadScore))}>
                <span className="font-bold">{lead.leadScore}</span>
                <span className="text-xs opacity-80">{getLeadScoreLabel(lead.leadScore)}</span>
              </div>
            </div>

            {/* Basic Info Section */}
            <div className="border border-slate-200/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('basic')}
                className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">Basic Information</span>
                </div>
                {expandedSections.basic ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              
              {expandedSections.basic && (
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Category</p>
                      <p className="font-medium text-slate-900 capitalize">{lead.category}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Subcategory</p>
                      <p className="font-medium text-slate-900 capitalize">{lead.subcategory || 'General'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Established</p>
                      <p className="font-medium text-slate-900">{lead.establishedYear || 'Unknown'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Employees</p>
                      <p className="font-medium text-slate-900">{lead.employeeCount || 'Unknown'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Revenue Range</p>
                      <p className="font-medium text-slate-900">{lead.annualRevenue || 'Unknown'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Description</p>
                      <p className="text-slate-700">{lead.description || 'No description available'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info Section */}
            <div className="border border-slate-200/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('contact')}
                className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">Contact Information</span>
                </div>
                {expandedSections.contact ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              
              {expandedSections.contact && (
                <div className="p-4 space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Address</p>
                      <p className="font-medium text-slate-900 truncate">{lead.address}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(lead.address, 'Address')}
                      className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                      title="Copy Address"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>

                  {lead.phone && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Phone className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Phone</p>
                        <p className="font-medium text-slate-900">{lead.phone}</p>
                      </div>
                      <button
                        onClick={() => lead.phone && copyToClipboard(lead.phone, 'Phone')}
                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Copy Phone"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {lead.email && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Mail className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Email</p>
                        <p className="font-medium text-slate-900 truncate">{lead.email}</p>
                      </div>
                      <button
                        onClick={() => lead.email && copyToClipboard(lead.email, 'Email')}
                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Copy Email"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}

                  {lead.website && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <Globe className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Website</p>
                        <a href={lead.website} target="_blank" rel="noopener noreferrer" className="font-medium text-primary-600 hover:underline truncate block">
                          {lead.website}
                        </a>
                      </div>
                      <a
                        href={lead.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
                        title="Open Website"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Marketing Gaps Section */}
            {lead.marketingGaps.length > 0 && (
              <div className="border border-slate-200/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('marketing')}
                  className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">Marketing Gaps</span>
                    <span className="badge badge-warning">{lead.marketingGaps.length} gaps</span>
                  </div>
                  {expandedSections.marketing ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>
                
                {expandedSections.marketing && (
                  <div className="p-4">
                    <div className="flex flex-wrap gap-2">
                      {lead.marketingGaps.map((gap, i) => (
                        <span key={i} className="badge badge-amber">
                          {gap}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Social Media Section */}
            {(lead.socialMedia?.instagram || lead.socialMedia?.facebook || lead.socialMedia?.twitter || lead.socialMedia?.linkedin) && (
              <div className="border border-slate-200/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('social')}
                  className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <GlobeIcon className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">Social Media</span>
                  </div>
                  {expandedSections.social ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>
                
                {expandedSections.social && (
                  <div className="p-4 space-y-2">
                    {lead.socialMedia?.instagram && (
                      <a
                        href={`https://instagram.com/${lead.socialMedia.instagram.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-pink-600" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Instagram</p>
                          <p className="font-medium text-slate-900 truncate">@{lead.socialMedia.instagram}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </a>
                    )}
                    {lead.socialMedia?.facebook && (
                      <a
                        href={`https://facebook.com/${lead.socialMedia.facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Facebook</p>
                          <p className="font-medium text-slate-900 truncate">{lead.socialMedia.facebook}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </a>
                    )}
                    {lead.socialMedia?.twitter && (
                      <a
                        href={`https://twitter.com/${lead.socialMedia.twitter}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-sky-600" fill="currentColor" viewBox="0 0 24 24"><path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/></svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">Twitter</p>
                          <p className="font-medium text-slate-900 truncate">@{lead.socialMedia.twitter}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </a>
                    )}
                    {lead.socialMedia?.linkedin && (
                      <a
                        href={`https://linkedin.com/company/${lead.socialMedia.linkedin}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <svg className="w-5 h-5 text-blue-700" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">LinkedIn</p>
                          <p className="font-medium text-slate-900 truncate">{lead.socialMedia.linkedin}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Hours Section */}
            {lead.hours && (
              <div className="border border-slate-200/50 rounded-xl overflow-hidden">
                <button
                  onClick={() => toggleSection('hours')}
                  className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-slate-600" />
                    <span className="font-medium text-slate-900">Business Hours</span>
                  </div>
                  {expandedSections.hours ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </button>
                
                {expandedSections.hours && (
                  <div className="p-4">
                    <div className="space-y-2">
                      {Object.entries(lead.hours!).map(([day, hours]) => (
                        <div key={day} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                          <span className="font-medium text-slate-700 capitalize">{day}</span>
                          <span className="text-slate-600">
                            {hours.open === 'closed' ? 'Closed' : `${hours.open} - ${hours.close}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Metadata Section */}
            <div className="border border-slate-200/50 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection('analytics')}
                className="w-full p-4 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-slate-600" />
                  <span className="font-medium text-slate-900">Metadata</span>
                </div>
                {expandedSections.analytics ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </button>
              
              {expandedSections.analytics && (
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Source</p>
                      <p className="font-medium text-slate-900 capitalize">{lead.source}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Last Updated</p>
                      <p className="font-medium text-slate-900">{formatRelativeTime(lead.lastUpdated)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Added</p>
                      <p className="font-medium text-slate-900">{formatDate(lead.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Coordinates</p>
                      <p className="font-medium text-slate-900 text-xs font-mono">
                        {lead.coordinates.lat.toFixed(6)}, {lead.coordinates.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 pt-2 border-t border-slate-200/50">
              {!lead.hasWebsite && (
                <button
                  onClick={loadTemplates}
                  disabled={isBuildingWebsite}
                  className="btn-primary gap-2"
                >
                  <Globe className="w-4 h-4" />
                  <span>{isBuildingWebsite ? 'Building...' : 'Build Website'}</span>
                  {isBuildingWebsite && <Loader2 className="w-4 h-4 animate-spin" />}
                </button>
              )}
              
              <button
                onClick={() => setShowCreateClient(true)}
                className="btn-secondary gap-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add to Pipeline</span>
              </button>

              <button
                onClick={handleEnrichLead}
                disabled={isEnriching}
                className="btn-ghost gap-2"
              >
                <ArrowUpRight className="w-4 h-4" />
                <span>{isEnriching ? 'Enriching...' : 'Enrich Lead'}</span>
                {isEnriching && <Loader2 className="w-4 h-4 animate-spin" />}
              </button>
            </div>
          </div>
        )}

        {/* Website Tab */}
        {activeTab === 'website' && (
          <div className="space-y-4 animate-fade-in">
            {lead.hasWebsite ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Check className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-green-800">Website Found</h3>
                      <p className="text-sm text-green-700">{lead.website}</p>
                    </div>
                  </div>
                </div>

                {lead.websiteQuality !== undefined && (
                  <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-700">Website Quality Score</span>
                      <span className={cn('font-bold', getLeadScoreColor(lead.websiteQuality || 0))}>
                        {lead.websiteQuality}/100
                      </span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={cn('h-full rounded-full', getLeadScoreColor(lead.websiteQuality || 0).replace('text-', 'bg-').replace('bg-', 'bg-'))}
                        style={{ width: `${lead.websiteQuality || 0}%` }}
                      />
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      {lead.websiteQuality && lead.websiteQuality > 70 
                        ? 'Modern, well-optimized website' 
                        : lead.websiteQuality && lead.websiteQuality > 40 
                        ? 'Website needs improvements' 
                        : 'Outdated or poorly optimized website'}
                    </p>
                  </div>
                )}

                <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                  <h4 className="font-medium text-slate-900 mb-3">Quick Actions</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <a
                      href={lead.website!}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary gap-2 justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      <span>Visit Site</span>
                    </a>
                    <button className="btn-ghost gap-2 justify-center">
                      <Download className="w-4 h-4" />
                      <span>Audit Site</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-amber-800">No Website Detected</h3>
                      <p className="text-sm text-amber-700">This business doesn't have a website - perfect opportunity!</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                  <h4 className="font-medium text-slate-900 mb-3">Build a Website</h4>
                  <p className="text-sm text-slate-600 mb-4">Choose a template and generate a professional website in minutes</p>
                  <button
                    onClick={loadTemplates}
                    disabled={isBuildingWebsite}
                    className="btn-primary w-full gap-2"
                  >
                    <Globe className="w-4 h-4" />
                    <span>{isBuildingWebsite ? 'Building...' : 'Choose Template & Build'}</span>
                    {isBuildingWebsite && <Loader2 className="w-4 h-4 animate-spin" />}
                  </button>
                </div>

                {/* Template Preview Cards */}
                {templates.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900">Available Templates</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {templates.map(template => (
                        <button
                          key={template.id}
                          onClick={() => {
                            setSelectedTemplate(template);
                            handleBuildWebsite();
                          }}
                          className={cn(
                            'p-3 border-2 rounded-xl transition-all text-left',
                            selectedTemplate?.id === template.id
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-slate-200 hover:border-slate-300'
                          )}
                        >
                          <div className="aspect-video bg-slate-100 rounded-lg mb-2 flex items-center justify-center">
                            <span className="text-3xl">{template.category === 'restaurant' ? '🍽️' : template.category === 'health' ? '🏥' : '🏢'}</span>
                          </div>
                          <h5 className="font-medium text-slate-900 text-sm">{template.name}</h5>
                          <p className="text-xs text-slate-500 truncate">{template.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contact Tab */}
        {activeTab === 'contact' && (
          <div className="space-y-4 animate-fade-in">
            <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-4">Contact Actions</h4>
              <div className="space-y-3">
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone.replace(/\D/g, '')}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                      <Phone className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Call Business</p>
                      <p className="text-sm text-slate-500">{lead.phone}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-400" />
                  </a>
                )}

                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">Send Email</p>
                      <p className="text-sm text-slate-500 truncate">{lead.email}</p>
                    </div>
                    <ArrowUpRight className="w-5 h-5 text-slate-400" />
                  </a>
                )}

                <button
                  onClick={() => setShowCreateClient(true)}
                  className="flex items-center gap-3 p-3 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                    <Plus className="w-5 h-5 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Add to Pipeline</p>
                    <p className="text-sm text-slate-600">Track as potential client</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-primary-600" />
                </button>

                <button className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Share2 className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Share Lead</p>
                    <p className="text-sm text-slate-500">Send to team member</p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-slate-400" />
                </button>
              </div>
            </div>

            {/* Notes Section */}
            <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-3">Notes</h4>
              <textarea
                placeholder="Add notes about this lead..."
                className="input-field min-h-[100px] resize-y"
                rows={4}
              />
              <div className="flex justify-end gap-2 mt-3">
                <button className="btn-secondary text-sm">Save Notes</button>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-4 animate-fade-in">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Lead Score</p>
                <p className={cn('text-3xl font-bold', getLeadScoreColor(lead.leadScore))}>{lead.leadScore}</p>
                <p className="text-xs text-slate-500 mt-1">{getLeadScoreLabel(lead.leadScore)}</p>
              </div>
              <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Est. Monthly Value</p>
                <p className="text-3xl font-bold text-slate-900">${Math.round(lead.leadScore * 2.5)}</p>
                <p className="text-xs text-slate-500 mt-1">Based on lead score</p>
              </div>
              <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Competition Level</p>
                <p className="text-3xl font-bold text-slate-900">
                  {lead.rating && lead.rating > 4.5 ? 'High' : lead.rating && lead.rating > 3.5 ? 'Medium' : 'Low'}
                </p>
                <p className="text-xs text-slate-500 mt-1">Based on reviews & rating</p>
              </div>
              <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
                <p className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">Conversion Probability</p>
                <p className="text-3xl font-bold text-slate-900">{lead.leadScore > 70 ? 'High' : lead.leadScore > 40 ? 'Medium' : 'Low'}</p>
                <p className="text-xs text-slate-500 mt-1">Estimated close rate</p>
              </div>
            </div>

            <div className="p-4 bg-white border border-slate-200/50 rounded-xl">
              <h4 className="font-medium text-slate-900 mb-3">Lead Insights</h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center">
                    <Target className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">High Intent Signals</p>
                    <p className="text-sm text-slate-500">
                      {lead.marketingGaps.includes('Website') && '• Actively needs a website'}
                      {lead.marketingGaps.includes('SEO') && '• Search visibility opportunity'}
                      {lead.marketingGaps.includes('Social Media') && '• No social presence'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">Revenue Potential</p>
                    <p className="text-sm text-slate-500">
                      {lead.annualRevenue === '1M-5M' || lead.annualRevenue === '5M+' 
                        ? 'High-value target ($1M+ revenue)' 
                        : lead.annualRevenue === '500K-1M'
                        ? 'Mid-market opportunity ($500K-1M)'
                        : 'Small business segment'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">Best Contact Time</p>
                    <p className="text-sm text-slate-500">Weekdays 10am-2pm (business hours)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Template Selector Modal */}
      {showTemplateSelector && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-scale-in">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Choose a Template</h3>
              <button onClick={() => setShowTemplateSelector(false)} className="p-2 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedTemplate(template);
                    handleBuildWebsite();
                  }}
                  disabled={isBuildingWebsite}
                  className={cn(
                    'p-4 border-2 rounded-xl transition-all text-left flex flex-col',
                    selectedTemplate?.id === template.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div className="aspect-video bg-slate-100 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                    <span className="text-4xl">{template.category === 'restaurant' ? '🍽️' : template.category === 'health' ? '🏥' : template.category === 'technology' ? '💻' : '🏢'}</span>
                    {selectedTemplate?.id === template.id && (
                      <div className="absolute inset-0 bg-primary-500/10 flex items-center justify-center">
                        <Check className="w-8 h-8 text-primary-500" />
                      </div>
                    )}
                  </div>
                  <h5 className="font-medium text-slate-900">{template.name}</h5>
                  <p className="text-sm text-slate-500 mt-1 flex-1">{template.description}</p>
                  <span className="badge badge-primary mt-2 self-start">{template.category}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setShowTemplateSelector(false)} className="btn-secondary">Cancel</button>
              <button 
                onClick={handleBuildWebsite} 
                disabled={!selectedTemplate || isBuildingWebsite}
                className="btn-primary"
              >
                {isBuildingWebsite ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Building...</span>
                  </>
                ) : (
                  <>
                    <Globe className="w-4 h-4" />
                    <span>Build Website</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Client Modal */}
      {showCreateClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-scale-in">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Add to Pipeline</h3>
              <button onClick={() => setShowCreateClient(false)} className="p-2 rounded-lg hover:bg-slate-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Build Fee (One-time)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={clientForm.buildFee}
                    onChange={(e) => setClientForm(prev => ({ ...prev, buildFee: parseInt(e.target.value) || 0 }))}
                    className="input-field pl-7"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Monthly Fee (Recurring)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                  <input
                    type="number"
                    value={clientForm.monthlyFee}
                    onChange={(e) => setClientForm(prev => ({ ...prev, monthlyFee: parseInt(e.target.value) || 0 }))}
                    className="input-field pl-7"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add notes about this opportunity..."
                  className="input-field min-h-[80px] resize-y"
                  rows={3}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowCreateClient(false)} className="btn-secondary">Cancel</button>
                <button onClick={handleCreateClient} className="btn-primary">
                  <Plus className="w-4 h-4" />
                  <span>Add Client</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

export default LeadDetailPanel;